import { useEffect, useState } from 'react';

import SignIn from './pages/account/signIn';
import {
  Routes,
  Route,
  useLocation,
  useSearchParams,
  useNavigate,
} from 'react-router-dom';
import useUserData, {
  useGetSideBarItemsFn,
} from './hooks/react-query/useUserData';
import { useGetCarList } from './hooks/react-query/useCarData';
import { queryClient } from './utils/queryClient';
import { instance } from './config/intercepter';
import usePersonStore from './store/personStore';

import Layout from './layouts/layout';
import Home from './components/home/home';
import NotFound from './components/route/notFound';
import RequireAuth from './components/route/requireAuth';
import { FinalizeOffer } from './pages/finalizeOffer';
import { FaqPage } from './pages/faq/FaqPage';
import CustomComponents from './components/CustomComponents';
import Drivers from './pages/drivers/drivers';
import CarList from './pages/my-car/carList';
import { InvoicePage } from './pages/dashboard/InvoicePage';
import { NotificationsGuide } from './pages/service/NotificationsGuide';
import ImportDrivers from './pages/drivers/importDrivers';
import { ServicePage } from './pages/service/ServicePage';
import UserRoles from './pages/user/roles';
import User from './pages/user/user';

import BookingReporting from './pages/admin/bookingReporting';
import OfferReporting from './pages/admin/offerReporting';
import ForgotPassword from './pages/account/forgotPassword';
import Account from './pages/account/account';
import ResetPassword from './pages/account/resetPassword';
import DownloadLogs from './pages/admin/downloadLogs';
import Reports from './pages/reports/reports';
import DashBoard from './pages/dashboard/DashBoard';
import Notifications from './pages/account/notifications';
import OfferCheck from './pages/admin/offerCheck';
import { KycRedirect } from './components/offer/kyc/KycRedirect';
import { GroupOfCompaniesLanding } from './pages/dashboard/GroupOfCompaniesLanding';
import { CompanyGroupsManagement } from './pages/admin/companyGroupsManagement';

const App = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const jwt = searchParams.get('jwt') || '';
  const { pathname, state } = location;
  const [showUser, setUserData] = useState(false);
  const [isUserLoggedIn, setUserLoggin] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const refreshSideBar = usePersonStore((store) => store.refreshSideBar);
  const fetchCarList = usePersonStore((store) => store.fetchCarList);

  useUserData(jwt, showUser);
  useGetSideBarItemsFn(jwt, refreshSideBar);
  useGetCarList('', 1, 6, fetchCarList);

  useEffect(() => {
    if (!isLoaded) {
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    showUser && setUserData(false);
  }, [location]);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    const fleetAdminJWT = localStorage.getItem('fleetAdminJWT');
    if (!isUserLoggedIn) {
      setUserLoggin(!!isLoggedIn);
      if(!!fleetAdminJWT) {
        localStorage.setItem('isLoggedIn', true);
        setUserLoggin(true);
        setUserData(true);
      }
    }
  }, [isUserLoggedIn]);

  useEffect(() => {
    if (
      pathname === '/forgot-password' ||
      pathname === '/passwordreset' || // need to check
      pathname === '/impersonate'
    ) {
      const fleetAdminJWT = localStorage.getItem('fleetAdminJWT');
      if (!showUser && !fleetAdminJWT) {
        handleLogout();
      }
    }
  }, [pathname, location, showUser]);

  useEffect(() => {
    if (!isUserLoggedIn) {
      if (pathname === '/impersonate' && !showUser) {
        localStorage.setItem('isLoggedIn', true);
        localStorage.setItem('jwt', jwt);

        if (state?.adminJWT) {
          localStorage.setItem('adminJWT', state.adminJWT);
          // navigate('/');
        }
        setUserData(true);
      }
    }

  }, [isUserLoggedIn, pathname, showUser, state, jwt]);

  const handleLogout = () => {
    setUserLoggin(false);
    queryClient.clear();
    instance.defaults.headers.common = {
      Authorization: null,
    };
    localStorage.removeItem('user-info');
    sessionStorage.clear();
    localStorage.clear();
  };

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route path="/" element={<SignIn />} />
        <Route path="/activateuser/:hash" element={<SignIn />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/passwordreset/*" element={<ResetPassword />} />
        <Route path="/components" element={<CustomComponents />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route element={<RequireAuth />}>
          <Route path="/home" element={<Home />} /> 
          <Route path="/dashboard" element={<DashBoard />} />
          <Route path="/portal/my-cars" element={<CarList />} />
          <Route path="/drivers" element={<Drivers />} />
          <Route path="/portal/import-drivers" element={<ImportDrivers />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/track-your-car/details/:orderId" element={<FinalizeOffer />} />
          <Route path='/KYC/callback' element={<KycRedirect/>} />
          <Route path="/faqs" element={<FaqPage />} />
          <Route
            path="/service/notifications_guide"
            element={<NotificationsGuide />}
          />
          <Route path="/groupfleetadmin" element={<GroupOfCompaniesLanding />} />
          <Route path="/group-of-companies-management" element={<CompanyGroupsManagement />} />
          <Route path="/invoices" element={<InvoicePage />} />
          <Route path="/service-booking" element={<ServicePage />} />
          <Route path="/service-booking/summary/:flowId" element={<ServicePage />} />
          <Route path="/portal/roles" element={<UserRoles />} />
          <Route
            path="/portal/booking-reports"
            element={<BookingReporting />}
          />
          <Route path="/portal/offers-reporting" element={<OfferReporting />} />
          <Route path="/portal/downloads" element={<DownloadLogs />} />
          <Route path="/portal/users" element={<User />} />
          <Route path="/portal/account" element={<Account />} />
          <Route path="/portal/offers-check" element={<OfferCheck />} />
        </Route>
        <Route path="*" element={<NotFound pathname={pathname} />} />
      </Route>
    </Routes>
  );
};

export default App;
